'use client'

import type React from 'react'

import { useRef } from 'react'

import {
  Dialog as BaseDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './base'

export interface DialogSplitProps {
  title?: React.ReactNode
  /** Main content for standard dialog view. Not required when using split view. */
  mainContent?: React.ReactNode
  description?: React.ReactNode
  footer?: React.ReactNode
  showCloseButton?: boolean
  onInteractOutside?: (event: Event) => void
  /** Used when the dialog is to be opened by clicking on another component */
  trigger?: React.ReactNode
  /** Used when programmatically opening or closing the dialog */
  open?: boolean
  /** Automatically called when programmatically opening or closing the dialog */
  onOpenChange?: (open: boolean) => void
  /** Height of the dialog content when the transition ends */
  transitionEndHeight?: number
  /** Condition to determine if the dialog should transition to the transitionEndHeight */
  transitionCondition?: boolean
  dialogContentClassName?: string
  /** Enable split view mode with content on the left and right sides */
  isSplitView?: boolean
  /** Content for the left side of the split view */
  leftContent?: React.ReactNode
  /** Content for the right side of the split view */
  rightContent?: React.ReactNode
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function DialogSplit({
  title,
  mainContent,
  description,
  footer,
  showCloseButton,
  onInteractOutside,
  trigger,
  open,
  onOpenChange,
  dialogContentClassName,
  isSplitView = false,
  leftContent,
  rightContent,
}: DialogSplitProps) {
  const contentRef = useRef<HTMLDivElement>(null)

  // Validate dialog configuration
  validateDialogConfiguration({
    trigger,
    open,
    onOpenChange,
    isSplitView,
    mainContent,
    leftContent,
  })

  return (
    <BaseDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        showCloseButton={showCloseButton}
        onInteractOutside={onInteractOutside}
        ref={contentRef}
        className={dialogContentClassName}
        isSplitView={isSplitView}
        style={{
          height: '100%',
          transition: 'height 0.3s ease-in-out',
        }}
      >
        {isSplitView ? (
          <SplitView
            title={title}
            description={description}
            leftContent={leftContent}
            rightContent={rightContent}
            footer={footer}
          />
        ) : (
          <StandardView
            title={title}
            description={description}
            mainContent={mainContent}
            footer={footer}
          />
        )}
      </DialogContent>
    </BaseDialog>
  )
}

interface ViewProps {
  title?: React.ReactNode
  description?: React.ReactNode
  footer?: React.ReactNode
}

interface StandardViewProps extends ViewProps {
  mainContent?: React.ReactNode
}

function StandardView({
  title,
  description,
  mainContent,
  footer,
}: StandardViewProps) {
  return (
    <>
      {title && (
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
      )}
      <span className="text-secondary">{mainContent}</span>
      {footer && <DialogFooter>{footer}</DialogFooter>}
    </>
  )
}

interface SplitViewProps extends ViewProps {
  leftContent?: React.ReactNode
  rightContent?: React.ReactNode
}

function SplitView({
  title,
  description,
  leftContent,
  rightContent,
  footer,
}: SplitViewProps) {
  return (
    <div className="flex flex-col h-full">
      {title && (
        <DialogHeader className="w-full mb-4" hidden>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
      )}
      <div className="flex flex-1 gap-4 min-h-0 overflow-hidden">
        <div className={`${rightContent ? 'w-[60%]' : 'w-full'} overflow-auto`}>
          {leftContent}
        </div>
        {rightContent && (
          <div className="w-[40%] overflow-auto">{rightContent}</div>
        )}
      </div>
      {footer && <DialogFooter className="mt-4">{footer}</DialogFooter>}
    </div>
  )
}

/**
 * Validates the dialog configuration to ensure proper usage
 */
function validateDialogConfiguration({
  trigger,
  open,
  onOpenChange,
  isSplitView,
  mainContent,
  leftContent,
}: Pick<
  DialogSplitProps,
  | 'trigger'
  | 'open'
  | 'onOpenChange'
  | 'isSplitView'
  | 'mainContent'
  | 'leftContent'
>) {
  const hasTrigger = !!trigger
  const hasOpenAndChange = open !== undefined && onOpenChange !== undefined

  if (hasTrigger === hasOpenAndChange) {
    throw new Error(
      'Dialog must have either trigger or both open and onOpenChange, but not both or neither',
    )
  }

  // Validate content requirements based on view mode
  if (!isSplitView && !mainContent) {
    throw new Error('Dialog must have mainContent when not in split view mode')
  }

  if (isSplitView && !leftContent) {
    throw new Error('Dialog must have leftContent when in split view mode')
  }
}
